import React, { useEffect, useState } from 'react';

import { Box, Popover, PopoverBody, PopoverContent, PopoverTrigger, Portal } from '@chakra-ui/react';
import { useToast } from 'components/hooks/useToast';
import { shallowEqual, useSelector } from 'react-redux';
import { REVIEW_RESULT_VIEW, useCurrentProvider, useSelectedReviewResultView } from 'reducers/reviewResultSlice';
import { useReviewFormSidebarDataOld } from 'screens/performanceReview/components/Sidebar/hooks/dataHooks';
import { selectLoggedInEmployeeData } from 'selectors/loggedInEmployee';
import { checkEmployeePersonaReviewReport, checkNewNavEnabled } from 'utils/helpers/featureFlags';
import { calculateDisplayEmail } from 'utils/helpers/netchexHelpers';

import { Email1 } from 'assets/assetsV1/iconComponents';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';

import { triggerPDFReportGeneration } from 'services/analytics/review';

import { Button } from 'uiToolkitV1/Button';
import { InputV2 } from 'uiToolkitV1/InputV2';
import { RadioSelect } from 'uiToolkitV1/RadioSelect';
import { TextLabel } from 'uiToolkitV1/Typography';

import useReviewFormParams from '../../hooks/useReviewFormParams';

const options = [
  {
    name: 'Current Reviewer View',
    enumVal: 'LOGGED_IN_EMP_VIEW',
  },
  {
    name: 'Self Reviewer View',
    enumVal: 'SELF_VIEW',
  },
];

const EmailPDFReport = () => {
  const toast = useToast();
  const selectedProviderData = useCurrentProvider();

  const [, setIsTriggeredPDF] = useState(false);
  const loggedInEmployee = useSelector(selectLoggedInEmployeeData, shallowEqual);
  const { employeeId: employeeIdParam, reviewId: reviewIdParam, providerId: providerIdParam } = useReviewFormParams();

  const { isLoading, data: formData = {} } = useReviewFormSidebarDataOld({
    employeeId: employeeIdParam,
    providerId: providerIdParam,
    reviewId: reviewIdParam,
  });

  const employeeEmail = calculateDisplayEmail(formData?.employee?.metadata ?? {});

  const [email, setEmail] = React.useState(calculateDisplayEmail(loggedInEmployee.metadata));
  const reportView = useSelectedReviewResultView();

  const [selectedRadioIndex, setSelectedRadioIndex] = useState(0);

  const isManagerProviderView = formData?.reviewInfo?.providerType === REVIEW_PROVIDER_TYPES.MANAGER;
  const isSelfViewReportDownLoadAvailable =
    reportView === REVIEW_RESULT_VIEW.QUESTION && checkEmployeePersonaReviewReport() && isManagerProviderView;

  useEffect(() => {
    setIsTriggeredPDF(false);
  }, [reportView]);

  const handlePDFGeneration = () => {
    triggerPDFReportGeneration({
      reviewId: reviewIdParam,
      providerId: selectedProviderData.id,
      responseId: selectedProviderData.reviewId,
      employeeId: employeeIdParam,
      receiverEmail: email,
      reportView,
      isDeerUI: checkNewNavEnabled(),
      // undefined so that it gets omitted in query params
      reportViewerPersona: isSelfViewReportDownLoadAvailable ? options[selectedRadioIndex].enumVal : undefined,
      employeeEmail,
    })
      .then(() => {
        setIsTriggeredPDF(true);
        toast({
          status: 'success',
          title: `You will receive PDF summary in ${email} inbox in few minutes`,
        });
      })
      .catch((e) => {
        toast({
          status: 'error',
          title: e.message || `PDF summary generation could not be triggered! Please try again later`,
        });

        console.error(e.message);
      });
  };

  const handleEmailChange = (event) => setEmail(event.target.value);

  return (
    <Popover placement="bottom-end">
      {({ onClose, isOpen }) => (
        <>
          <PopoverTrigger>
            <Button ml={3} variant="tertiary" StartIcon={Email1} height="32px" isDropdown selected={isOpen}>
              Email Report
            </Button>
          </PopoverTrigger>
          <Portal>
            <PopoverContent>
              <PopoverBody py={3} px={4}>
                {isSelfViewReportDownLoadAvailable && (
                  <>
                    <TextLabel color="text.g2">Download Version</TextLabel>
                    <Box pl="6px">
                      <RadioSelect
                        options={options}
                        selectedIndex={selectedRadioIndex}
                        onChange={setSelectedRadioIndex}
                        isVertical
                        containerProps={{ mt: 2, mb: 4 }}
                      />
                    </Box>
                  </>
                )}
                <TextLabel color="text.g2">Email</TextLabel>
                <InputV2 label="Email" id="email" isDashed defaultValue={email} onChange={handleEmailChange} />
                <Button
                  onClick={() => {
                    handlePDFGeneration();
                    onClose();
                  }}
                  mt={8}
                  width="100%"
                  isDisabled={isLoading}
                >
                  Email
                </Button>
              </PopoverBody>
            </PopoverContent>
          </Portal>
        </>
      )}
    </Popover>
  );
};

export default EmailPDFReport;
